/**
 * nova-palette.ts — Nova brand palette.
 * Source of truth: SPEC.md / Nova Character Bible. Do not hand-tune.
 */
export const NOVA_COLORS = {
  body: "#2D6E70",
  bodyDark: "#1F5759",
  deep: "#1C1A17",
  ivory: "#F4EFE6",
  blue: "#A8C8E8",
  orange: "#D4541F",
  mustard: "#D4A02C",
  coral: "#E8A98D",
  /** Recessed navy face-screen background */
  screen: "#0E1626",
} as const;

export type NovaPaletteKey = keyof typeof NOVA_COLORS;

/**
 * NOVA-LOAD-1 — the loader rainbow.
 *
 * WHY these live here and not in the loader: they are the one piece of
 * the pre-2026 hand-drawn loader that survived the character rewrite.
 * Rennan asked for the new Nova with the OLD loader's colours, so these
 * six values are lifted byte-for-byte out of `NovaThinking.tsx` and are
 * not to be re-derived from NOVA_COLORS or "harmonised" with the brand
 * palette. Index = eye-block build order (0 = bottom-left … 5 = apex),
 * which is also the order the antenna ball steps through them.
 *
 * The matching antenna-ball keyframes live in `nova.css`
 * (`.nova-rainbow-ball`) — keep the two in sync by hand.
 */
export const NOVA_RAINBOW = [
  "rgb(237, 82, 82)",   // 0 red
  "rgb(242, 140, 51)",  // 1 orange
  "rgb(245, 211, 76)",  // 2 yellow
  "rgb(102, 199, 115)", // 3 green
  "rgb(76, 153, 235)",  // 4 blue
  "rgb(158, 102, 217)", // 5 violet (apex)
] as const;

/** Shade a hex color toward white (pct > 0) or black (pct < 0). */
export function shadeHex(hex: string, pct: number): string {
  const n = hex.replace("#", "");
  const r = parseInt(n.substring(0, 2), 16);
  const g = parseInt(n.substring(2, 4), 16);
  const b = parseInt(n.substring(4, 6), 16);
  const t = pct < 0 ? 0 : 255;
  const p = Math.abs(pct);
  const c = (v: number) =>
    Math.round(v + (t - v) * p)
      .toString(16)
      .padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}
